import * as React from 'react';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LeftImg from './common/LeftImg';
import { Divider } from '@mui/material';
import GoogleSignUp from './GoogleSignUp';
import Captcha from './common/Captcha';
import './common/Styles.css'
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import DebugService from '../services/DebugService';
import {
  CONST_SITE_LABEL,
  MESSAGES,
  ONBOARD_STATUS,
  REGISTRATION_TYPE,
  STORAGE_KEYS,
  navigateToDashboardPostLogin,
  parseJwt,
  validateEmail,
  validatePassword
} from './common/Helper';
import RegistrationApiService from '../services/RegistrationApiService';
import { useAuth } from '../state/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

/**
 * Sign Up (Registration) Component
 * @returns 
 */
export default function SignUp() {
  const [isInvalidEmail, setIsInvalidEmail] = React.useState(false);
  const [isInvalidFName, setIsInvalidFName] = React.useState(false);
  const [isInvalidLName, setIsInvalidLName] = React.useState(false);
  const [isInvalidPassword, setIsInvalidPassword] = React.useState(false);
  const [errors, setErrors] = React.useState({
    email: '',
    fname: '',
    lname: '',
    password: ''
  });
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [apiErrorMessage, setAPIErrorMessage] = React.useState('');
  const [apiErrorMessageForGoogle, setAPIErrorMessageForGoogle] = React.useState('');
  const [inProgress, setInProgress] = React.useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = React.useState(false);
  const [captchaError, setCaptchaError] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const captchaRef = React.useRef(null);

  const handleCaptchaVerify = (isValid) => {
    setIsCaptchaVerified(isValid)
    if (isValid) {
      setCaptchaError('')
    }
  }

  const {
    loginWithApi,
    loginWithGoogle,
    setIsOnboarded,
    isOnboarded
  } = useAuth();

  const navigate = useNavigate()

  const { setScopedItem, getScopedItem } = useScopedLocalStorage()

  /**
   * Form submit handler
   * @param {*} event 
   */
  const handleSubmit = (event) => {
    event.preventDefault();

    setIsInvalidFName(false)
    setIsInvalidLName(false)
    setIsInvalidEmail(false)
    setIsInvalidPassword(false)

    // Reset API error message
    setAPIErrorMessage('')
    setCaptchaError('')
    setInProgress(true)

    const data = new FormData(event.currentTarget);
    DebugService.log({
      fname: data.get('fname'),
      lname: data.get('lname'),
      email: data.get('email'),
      password: data.get('password'),
    });

    const newErrors = {}

    let isValidFName = true
    let isValidLName = true

    if (data.get('fname').length === 0) {
      isValidFName = false
      setIsInvalidFName(true)
      newErrors['fname'] = MESSAGES.FNAME_REQ
      setErrors(newErrors)
    }

    if (data.get('lname').length === 0) {
      isValidLName = false
      setIsInvalidLName(true)
      newErrors['lname'] = MESSAGES.LNAME_REQ
      setErrors(newErrors)
    }

    const isValidEmail = validateEmail(data.get('email'))
    if (!isValidEmail) {
      setIsInvalidEmail(true)
      newErrors['email'] = MESSAGES.EMAIL_VALID
      setErrors(newErrors)
    }

    const isValidPassword = validatePassword(data.get('password'))
    if (!isValidPassword) {
      setIsInvalidPassword(true)
      newErrors['password'] = MESSAGES.PASSWORD_VALID
      setErrors(newErrors)
    }

    const captchaInput = (data.get('captchaInput') || '').trim();
    let isCaptchaValid = false;
    if (!captchaInput) {
      setCaptchaError('Please fill captcha');
    } else if (!isCaptchaVerified) {
      setCaptchaError('Captcha mismatch');
    } else {
      isCaptchaValid = true;
    }

    if (isValidEmail && isValidFName && isValidLName && isValidPassword && isCaptchaValid) {
      // setShowSuccessMessage(true)

      const requestBody = {
        "firstname": data.get('fname'),
        "lastname": data.get('lname'),
        "email": data.get('email'),
        "password": data.get('password'),
      }

      // Call registration api is form is valid
      RegistrationApiService.manual(requestBody)
        .then(response => {
          DebugService.log(`Sign up component: ${REGISTRATION_TYPE.MANUAL} Registration API response`, response)

          if (response && response['data']) {
            const loginToken = response['data']['loginToken']
            if (loginToken) {
              // Navigate on receiving token
              loginWithApi(loginToken)
              setScopedItem('userID', response['data']['userId'].toString())
              setScopedItem('email', requestBody['email'].toString())
              setIsOnboarded(response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED)
              navigateToDashboardPostLogin(
                navigate,
                response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED
              )
            }
          } else if (response && response['error']) {
            const errMessage = response['error']['message']
            if (errMessage) {
              // Display error message to the user
              setAPIErrorMessage(errMessage)
            } else {
              DebugService.warn(`Sign up component: response.error not found`, response)
            }
          } else {
            DebugService.warn(`Sign up component: response.data not found`, response)
          }
        })
        .catch(e => {
          DebugService.error(`Sign up component: Error caught ${REGISTRATION_TYPE.MANUAL} while registration`, e)
        })
        .finally(() => {
          setInProgress(false)
          captchaRef.current?.refresh()
        })
    } else {
      setInProgress(false)
    }
  };

  /**
   * Google sign up success handler
   * @param {*} data 
   */
  const googleSuccessHandle = (data) => {
    // Reset API error message
    setAPIErrorMessageForGoogle('')

    const userData = parseJwt(data['credential'])
    const jsonData = data

    if (userData['email'] && userData['email'] !== '') {
      DebugService.log('Sign up component: User data', userData)
      jsonData['user_info'] = userData

      const requestBody = {
        "firstname": userData['given_name'],
        "lastname": userData['family_name'],
        "email": userData['email'],
        "googleLoginToken": 'response.credential',
        "googleJson": JSON.stringify(jsonData)
      }

      // Call registration api is form is valid
      RegistrationApiService.google(requestBody)
        .then(response => {
          DebugService.log(`Sign up component: ${REGISTRATION_TYPE.GOOGLE} Registration API response`, response)

          if (response && response['data']) {
            const loginToken = response['data']['loginToken']
            if (loginToken) {
              // Navigate on receiving token
              loginWithGoogle(loginToken)
              setScopedItem('userID', response['data']['userId'].toString())
              setScopedItem('email', requestBody['email'].toString())
              setIsOnboarded(response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED)
              navigateToDashboardPostLogin(
                navigate,
                response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED
              )
            }
          } else if (response && response['error']) {
            const errMessage = response['error']['message']
            if (errMessage) {
              // Display error message to the user
              setAPIErrorMessageForGoogle(errMessage)
            } else {
              DebugService.warn(`Sign up component: response.error not found`, response)
            }
          } else {
            DebugService.warn(`Sign up component: response.data not found`, response)
          }
        })
        .catch(e => {
          DebugService.error(`Sign up component: Error caught ${REGISTRATION_TYPE.GOOGLE} while registration`, e)
        })
        .finally(() => setInProgress(false))
    }
  }

  /**
   * Google sign up failure handler
   * @param {*} error 
   */
  const googleFailureHandle = (error) => {
    DebugService.error(`Sign up component: Error caught while google sign up`, error)
  }

  React.useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Sign Up`

    if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
      navigateToDashboardPostLogin(navigate, isOnboarded)
    }
  }, [])

  return (
    <Box className='sign-in-container'>
      <Grid container>

        {/* Left Panel */}
        <Grid item xs={12} md={6}>
          <LeftImg />
        </Grid>

        {/* Right Panel */}
        <Grid item xs={12} md={6} className='form-grid'>
          <div className='form-container'>
            <Typography variant='h4' textAlign={'center'}>Create An Account</Typography>
            <div><Typography variant='subtitle2' color={'#84818A'}>
              Already have an account?&nbsp;
              <Link href={CONST_ROUTE_PATH.SIGN_IN} color={'#4285F4'} >Sign In</Link>
            </Typography></div>

            {/* Google sign In */}
            {process.env?.REACT_APP_DISABLE_GOOGLE_SIGNIN !== 'true' && (
              <>
                <GoogleSignUp
                  handleSuccess={googleSuccessHandle}
                  handleFailure={googleFailureHandle}
                />
                {/* Show API error message for Google sign in */}
                {apiErrorMessageForGoogle.length > 0 && (
                  <Typography color={'#D32F2F'}>{apiErrorMessageForGoogle}</Typography>
                )}
                <Divider style={{ width: '100%', color: '#84818A' }}>or</Divider>
              </>
            )}
            {/* Manual Sign In */}
            <Grid container spacing={2} component="form" noValidate onSubmit={handleSubmit}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  id="fname"
                  label="First Name"
                  name="fname"
                  autoComplete="fname"
                  autoFocus
                  error={isInvalidFName}
                  helperText={errors.fname}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  id="lname"
                  label="Last Name"
                  name="lname"
                  autoComplete="lname"
                  error={isInvalidLName}
                  helperText={errors.lname}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="off"
                  error={isInvalidEmail}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="password"
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="off"
                  error={isInvalidPassword}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((show) => !show)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* CAPTCHA */}
              <Grid item xs={12}>
                <Captcha ref={captchaRef} onVerify={handleCaptchaVerify} />
                {captchaError.length > 0 && (
                  <Typography className='signin-captcha-error' color={'#D32F2F'} variant='caption'>
                    {captchaError}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                {/* // Show API error message */}
                {apiErrorMessage.length > 0 && (
                  <Typography color={'#D32F2F'}>{apiErrorMessage}</Typography>
                )}

                {/* Sign In Button */}
                <LoadingButton
                  // onClick={() => validateOnboarding()}
                  className='submit-btn'
                  loading={inProgress}
                  loadingPosition="end"
                  variant="contained"
                  fullWidth
                  type='submit'
                >
                  Sign Up
                </LoadingButton>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
    </Box>
  );
}